import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useEffect, useRef, useState, FormEvent } from 'react';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

import { BACKEND_URL } from './apiBase';
import { speakText, stopSpeaking, isMuted, setMuted } from './tts';
import './ChatBox.css';

interface ChatBoxProps {
  app_state: string;
  onUpdateMessage: (message: string) => void;
}

interface TermLine {
  role: 'user' | 'system';
  text: string;
}

/**
 * Terminal-style AI console: a scrollback of user questions (cyan `>`
 * prompt) and system answers (green `bluesky:` prompt). Questions arrive
 * either typed into the input row or spoken via the mic (the robot tile
 * drives app_state); both route to the same backend /chat call, and
 * answers are optionally read aloud via tts.
 */
function ChatBox({ app_state, onUpdateMessage }: ChatBoxProps) {
  const [history, setHistory] = useState<TermLine[]>([]);
  const [draft, setDraft] = useState('');
  const [processing, setProcessing] = useState(false);
  const [muted, setMutedState] = useState(isMuted());

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const prevListening = useRef(listening);
  const bodyRef = useRef<HTMLDivElement>(null);

  const ask = (question: string) => {
    const q = question.trim();
    if (!q || processing) return;
    setHistory((h) => [...h, { role: 'user', text: q }]);
    setProcessing(true);
    onUpdateMessage("processing");
    fetch(`${BACKEND_URL}/chat?question=${encodeURIComponent(q)}`, {
      method: "get",
    })
      .then(res => res.json())
      .then(text => {
        const answer = typeof text === 'string' ? text : String(text);
        setHistory((h) => [...h, { role: 'system', text: answer }]);
        speakText(answer);
        onUpdateMessage("active");
      })
      .catch(err => {
        console.error("Error fetching chat response:", err);
        setHistory((h) => [...h, { role: 'system', text: "Error fetching response." }]);
        onUpdateMessage("active");
      })
      .finally(() => setProcessing(false));
  };

  // Keep the voice-edge effect on the freshest ask() without making it a
  // dependency (would re-fire the listening edge on every render).
  const askRef = useRef(ask);
  useEffect(() => { askRef.current = ask; });

  useEffect(() => {
    if (app_state === "listening") {
      stopSpeaking();   // don't let the previous reply talk over the question
      resetTranscript();
      if (browserSupportsSpeechRecognition) {
        SpeechRecognition.startListening();
      }
    }
  }, [app_state, resetTranscript, browserSupportsSpeechRecognition]);

  useEffect(() => {
    // When listening changes from true to false and there is a transcript,
    // the utterance joins the console exactly like a typed question.
    if (prevListening.current && !listening && transcript) {
      askRef.current(transcript);
    }
    prevListening.current = listening;
  }, [listening, transcript]);

  // Keep the newest line in view, terminal-style.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [history, processing, listening]);

  // Never leave audio playing after the chat component goes away.
  useEffect(() => stopSpeaking, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (next) stopSpeaking();
    setMutedState(next);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    ask(draft);
    setDraft('');
  };

  return (
    <div id="ChatBox" className="terminal" aria-label="AI console">
      <button
        type="button"
        className="tts-toggle"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute voice responses' : 'Mute voice responses'}
        title={muted ? 'Voice is off' : 'Voice is on'}
      >
        {muted ? <FaVolumeMute /> : <FaVolumeUp />}
      </button>

      <div className="terminal-body" ref={bodyRef}>
        {history.length === 0 && (
          <div className="term-line note">
            {browserSupportsSpeechRecognition
              ? 'welcome to the bluesky console — type a question below, or hit the robot tile to talk.'
              : 'browser does not support speech recognition — type your question below.'}
          </div>
        )}
        {history.map((line, i) => (
          <div key={i} className={`term-line ${line.role}`}>
            <span className="term-prompt" aria-hidden="true">
              {line.role === 'user' ? '>' : 'bluesky:'}
            </span>
            <span className="term-text">{line.text}</span>
          </div>
        ))}
        {app_state === 'listening' && (
          <div className="term-line note">
            <span className="term-prompt" aria-hidden="true">~</span>
            <span className="term-text">
              {transcript || 'begin speaking into your microphone now'}
            </span>
          </div>
        )}
        {processing && <div className="term-line note">thinking…</div>}
      </div>

      <form className="term-input-row" onSubmit={onSubmit}>
        <span className="term-prompt" aria-hidden="true">&gt;</span>
        <input
          className="term-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="type a message…"
          aria-label="Message the AI persona"
          disabled={processing}
        />
      </form>
    </div>
  );

};

export default ChatBox;
